import { yupArray, yupBoolean, yupNumber, yupObject, yupString } from '../../yup';
import { ErhlLoanPurpose } from '@/helpers/const';
import { getMinDateAfterBusinessDays } from '@/helpers/businessDays';

const ADDITIONAL_PURPOSES_NO = 'no' as const;

/** Returns true when additional purposes other than "No" are selected */
const hasAdditionalPurposes = (purposes?: string[]) =>
  Array.isArray(purposes) && purposes.length > 0 && !purposes.includes(ADDITIONAL_PURPOSES_NO);

/**
 * Validation schema for the ERHL Loan Details step.
 * @param maxScheduledEquityReleaseDuration - Age-based maximum monthly drawdown duration, in years
 * @param hasAdditionalContributions - Whether the applicant entered a non-zero "additional
 * contributions amount" on the property details page — when true, the additional borrowing
 * purposes/amount questions are suppressed entirely (contributions already cover the gap).
 */
export const erhlLoanDetailsSchema = (maxScheduledEquityReleaseDuration: number, hasAdditionalContributions: boolean) =>
  yupObject({
    erhlAdditionalBorrowingPurposes: yupArray(yupString().required())
      .when('isPurchaseApplication', {
        is: (isPurchase: boolean) => isPurchase && !hasAdditionalContributions,
        then: (s) => s.min(1, 'Please select at least one option').required(),
        otherwise: (s) => s.optional(),
      })
      .label('Additional borrowing purposes'),

    erhlLoanPurposeOtherDescription: yupString()
      .when(['isPurchaseApplication', 'erhlAdditionalBorrowingPurposes'], {
        is: (isPurchase: boolean, purposes: string[]) =>
          isPurchase &&
          !hasAdditionalContributions &&
          Array.isArray(purposes) &&
          purposes.includes(ErhlLoanPurpose.OTHER),
        then: (s) => s.required(),
        otherwise: (s) => s.optional(),
      })
      .label('Details of additional borrowing purposes'),

    erhlAdditionalAmountToBorrow: yupNumber()
      .when(['isPurchaseApplication', 'erhlAdditionalBorrowingPurposes'], {
        is: (isPurchase: boolean, purposes: string[]) =>
          isPurchase && !hasAdditionalContributions && hasAdditionalPurposes(purposes),
        then: (s) => s.min(0).required(),
        otherwise: (s) => s.optional(),
      })
      .label('Additional amount required'),

    /** Non-purchase only: upfront lump sum amount (non-blocking, no minimum enforced). */
    lumpSumAmount: yupNumber()
      .min(0)
      .when('isPurchaseApplication', {
        is: false,
        then: (s) => s.optional(),
        otherwise: (s) => s.strip(),
      })
      .label('Funds required'),

    /** Non-purchase only: requested settlement date for the lump sum. */
    lumpSumFundsRequiredBy: yupString()
      .when('isPurchaseApplication', {
        is: false,
        then: (s) =>
          s
            .isoDate()
            .test(
              'min-business-days',
              'Date must be at least 5 business days from now to allow time for processing',
              (value) => {
                if (!value) return true;
                const minDate = getMinDateAfterBusinessDays(5);
                return new Date(value) >= minDate;
              },
            )
            .required()
            .label('Funds required by'),
        otherwise: (s) => s.strip(),
      })
      .label('Funds required by'),

    hasScheduledEquityRelease: yupBoolean().required().label('Scheduled equity release'),

    // Max $20,000 is the slider's visual range only — no hard ceiling. Values above the
    // $12,500 policy maximum surface a non-blocking advisory notice in the template instead.
    scheduledEquityReleaseAmount: yupNumber().when('hasScheduledEquityRelease', {
      is: true,
      then: (s) => s.min(1_000, 'Minimum scheduled equity release is $1,000.').required().label('Release amount'),
      otherwise: (s) => s.optional(),
    }),

    // Min 1 year is just the slider floor — terms below the 10-year policy minimum surface a
    // non-blocking advisory notice in the template instead of a validation error.
    durationTotalYears: yupNumber().when('hasScheduledEquityRelease', {
      is: true,
      then: (s) =>
        s
          .min(1, 'Minimum term is 1 year.')
          .max(maxScheduledEquityReleaseDuration, `Maximum term is ${maxScheduledEquityReleaseDuration} years.`)
          .required()
          .label('Release term'),
      otherwise: (s) => s.optional(),
    }),
  });
